import { v4 as uuid } from 'uuid';
import { from, map, mergeMap, of, switchMap, forkJoin, Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { Actions, ofActionCompleted, Store } from '@ngxs/store';

import * as FlatModels from '@medsurf/flat-models';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatFacades from '../../internal';

@Injectable()
export class SlideAuthorFacade {
  constructor(protected store: Store,
              protected actions$: Actions) {
  }

  //<editor-fold desc="Selectors">

  theme$ = this.store.select(FlatStates.SlideAuthorState.theme$);
  layout$ = this.store.select(FlatStates.SlideAuthorState.layout$);
  mediaPanelData$ = this.store.select(FlatStates.SlideAuthorState.mediaPanelData$);
  chapterPanelData$ = this.store.select(FlatStates.SlideAuthorState.chapterPanelData$);
  layerPanelData$ = this.store.select(FlatStates.SlideAuthorState.layerPanelData$);
  wysiwygPanelData$ = this.store.select(FlatStates.SlideAuthorState.wysiwygPanelData$);
  editPanelData$ = this.store.select(FlatStates.SlideAuthorState.editPanelData$);
  typedNodeExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedNodeExpanded$);
  typedPageExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedPageExpanded$);
  typedSlideLayersExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedSlideLayersExpanded$);
  typedSlideAnnotationsExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedSlideAnnotationsExpanded$);
  typedSlideGroupsExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedSlideGroupsExpanded$);
  typedSlidePoisExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedSlidePoisExpanded$);
  typedSlideQuestionsExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedSlideQuestionsExpanded$);
  typedLayerExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedLayerExpanded$);
  typedImageExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedImageExpanded$);
  typedImageAnnotationExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedImageAnnotationExpanded$);
  typedImagePoiExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedImagePoiExpanded$);
  typedGroupExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedGroupExpanded$);
  typedPoiExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedPoiExpanded$);
  typedPoiAnnotationExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedPoiAnnotationExpanded$);
  typedQuestionExpanded$ = this.store.select(FlatStates.SlideAuthorState.typedQuestionExpanded$);

  currentSelectedNode$ = this.store.select(FlatStates.NodeEntityState.currentSelectedNode$);
  currentSelectedSlideNode$ = this.store.select(FlatStates.NodeEntityState.currentSelectedSlideNode$);
  currentSelectedSlidePage$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlidePage$);
  currentSelectedSlide$ = this.store.select(FlatStates.PageEntityState.currentSelectedSlidePage$) as Observable<FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training>;
  currentSelectedRouteElement$ = this.store.select(FlatStates.NodeEntityState.currentSelectedRouteElement$);
  currentSelectedSlideLayer$ = this.store.select(FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$);
  currentSelectedSlideImages$ = this.store.select(FlatStates.ImageEntityState.currentSelectedSlideImages$);
  currentSelectedSlideImage$ = this.store.select(FlatStates.ImageEntityState.currentSelectedSlideImage$);
  currentSelectedSlideMedia$ = this.store.select(FlatStates.MediaEntityState.currentSelectedSlideMedia$);
  currentSelectedSlideGroup$ = this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroup$);
  currentSelectedSlideImageAreaGroup$ = this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideImageAreaGroup$);
  currentSelectedSlidePoi$ = this.store.select(FlatStates.PointOfInterestEntityState.currentSelectedSlidePoi$);
  currentSelectedImagePoi$ = this.store.select(FlatStates.PointOfInterestEntityState.currentSelectedImagePoi$);
  currentSelectedImagePois$ = this.store.select(FlatStates.PointOfInterestEntityState.currentSelectedImagePois$);
  currentSelectedSlideImageInteractiveAreas$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideImageInteractiveAreas$);
  currentSelectedSlideImageAreaGroups$ = this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideImageAreaGroups$);
  currentSelectedSlideAnnotation$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideAnnotation$);
  currentSelectedSlideAnnotationLabel$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideAnnotationLabel$);
  currentSelectedSlideAnnotationSource$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideAnnotationSource$);
  currentSelectedSlideMarkerTargets$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideMarkerTargets$);
  currentSelectedSlideMarkerFirstTarget$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideMarkerFirstTarget$);
  currentSelectedSlideFreeFormTarget$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideFreeFormTarget$);
  currentSelectedSlideFreeFormStyle$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideFreeFormStyle$);
  currentSelectedSlideKeymap$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideKeymap$);
  currentSelectedSlideKeymapStyle$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideKeymapStyle$);
  currentSelectedSlideKeymapColumns$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideKeymapColumns$);
  currentSelectedSlideQuestion$ = this.store.select(FlatStates.QuestionEntityState.currentSelectedSlideQuestion$);

  layerIndex$ = this.store.select(FlatStates.SlideLayerEntityState.layerIndex$);
  imageIndex$ = this.store.select(FlatStates.ImageEntityState.imageIndex$);
  defaultIndex$ = this.store.select(FlatStates.ImageEntityState.currentSelectedImagesDefaultIndex$);

  currentSelectedSlideImageAnnotationIds$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideImageAnnotationIds$);
  
  typedGroups$ = this.store.select(FlatStates.AnnotationGroupEntityState.typedEntities$);
  typedPois$ = this.store.select(FlatStates.PointOfInterestEntityState.typedEntities$);
  
  nodeTitleArray$ = this.store.select(FlatStates.PageEntityState.nodeTitleArray$);
  modalityArray$ = this.store.select(FlatStates.ModalityEntityState.modalityArray$);
  slideLayersArray$ = this.store.select(FlatStates.SlideLayerEntityState.slideLayersArray$);
  slideImagesArray$ = this.store.select(FlatStates.ImageEntityState.slideImagesArray$);
  slideGroupsArray$ = this.store.select(FlatStates.AnnotationGroupEntityState.slideGroupsArray$);

  currentSelectedSlideQuestions$ = this.store.select(FlatStates.QuestionEntityState.currentSelectedSlideQuestions$);
  currentSelectedSlideIsFreeForm$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideIsFreeForm$);
  currentSelectedSlideFreeFormIsFree$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideFreeFormIsFree$);
  currentSelectedSlideFreeFormIsInteractiveArea$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideFreeFormIsInteractiveArea$);
  currentSelectedSlideFreeFormIsShape$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideFreeFormIsShape$);
  currentSelectedSlideFreeFormIsLine$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideFreeFormIsLine$);
  currentSelectedSlideFreeFormIsDrawable$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideFreeFormIsDrawable$);
  currentSelectedSlideFreeFormTypeArray$ = this.store.select(FlatStates.AnnotationEntityState.currentSelectedSlideFreeFormTypeArray$);
  
  editPanelOverlay$ = this.store.select(FlatStates.SlideAuthorState.editPanelOverlay$);
  editPanelDisplay$ = this.store.select(FlatStates.SlideAuthorState.editPanelDisplay$);
  editPanelBulkEdit$ = this.store.select(FlatStates.SlideAuthorState.editPanelBulkEdit$);
  mainPanelDisplay$ = this.store.select(FlatStates.SlideAuthorState.mainPanelDisplay$);
  isSplitterMoving$ = this.store.select(FlatStates.SlideAuthorState.isSplitterMoving$);
  
  previousAuthorSlideUrl$ = this.store.select(FlatStates.PageEntityState.previousAuthorSlideUrl$);
  nextAuthorSlideUrl$ = this.store.select(FlatStates.PageEntityState.nextAuthorSlideUrl$);
  
  previousSlideUrl$ = this.store.select(FlatStates.PageEntityState.previousSlideUrl$);
  nextSlideUrl$ = this.store.select(FlatStates.PageEntityState.nextSlideUrl$);
  
  currentSelectedSlidePoisArray$ = this.store.select(FlatStates.PointOfInterestEntityState.currentSelectedSlidePoisArray$);
  currentSelectedSlideGroupsArray$ = this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroupsArray$);

  //</editor-fold>

  //<editor-fold desc="Lazy Selectors">

  node$ = (id: string) => this.store.select(FlatStates.NodeEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  page$ = (id: string) => this.store.select(FlatStates.PageEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  layer$ = (id: string) => this.store.select(FlatStates.SlideLayerEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  layers$ = (ids: string[]) => this.store.select(FlatStates.SlideLayerEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  group$ = (id: string) => this.store.select(FlatStates.AnnotationGroupEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  groups$ = (ids: string[]) => this.store.select(FlatStates.AnnotationGroupEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  poi$ = (id: string) => this.store.select(FlatStates.PointOfInterestEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  pois$ = (ids: string[]) => this.store.select(FlatStates.PointOfInterestEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  question$ = (id: string) => this.store.select(FlatStates.QuestionEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  questions$ = (ids: string[]) => this.store.select(FlatStates.QuestionEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  choice$ = (id: string) => this.store.select(FlatStates.ChoiceEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  choices$ = (ids: string[]) => this.store.select(FlatStates.ChoiceEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  image$ = (id: string) => this.store.select(FlatStates.ImageEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  images$ = (ids: string[]) => this.store.select(FlatStates.ImageEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  annotation$ = (id: string) => this.store.select(FlatStates.AnnotationEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  annotations$ = (ids: string[]) => this.store.select(FlatStates.AnnotationEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  annotationToPointOfInterests$ = (annotationToPointOfInterests: FlatModels.PointOfInterestEntityModels.AnnotationToPointOfInterest[]) => this.store.select(FlatStates.AnnotationEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(annotationToPointOfInterests.map(annotationToPointOfInterest => annotationToPointOfInterest.annotation) as unknown as string[])));
  label$ = (id: any) => this.store.select(FlatStates.AnnotationLabelEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  target$ = (id: any) => this.store.select(FlatStates.AnnotationTargetEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  targets$ = (ids: any) => this.store.select(FlatStates.AnnotationTargetEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  keymapLabel$ = (id: any) => this.store.select(FlatStates.KeymapLabelEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  keymapLabels$ = (ids: any) => this.store.select(FlatStates.KeymapLabelEntityState.entitiesByIds$).pipe(map(filterFn => filterFn(ids)));
  
  currentSelectedSlideImagesArray$ = (translate: TranslateService, imageIds: string[]) => this.store.select(FlatStates.ImageEntityState.currentSelectedSlideImagesArray$).pipe(map(filterFn => filterFn(translate, imageIds)));
  annotationOptionArray$ = (translate: TranslateService) => this.store.select(FlatStates.AnnotationEntityState.annotationOptionArray$).pipe(map(filterFn => filterFn(translate)));
  imageAnnotationOptionArray$ = (image: FlatModels.ImageEntityModels.Image, translate: TranslateService) => this.store.select(FlatStates.AnnotationEntityState.imageAnnotationOptionArray$).pipe(map(filterFn => filterFn(image, translate)));
  imagePoiOptionArray$ = (image: FlatModels.ImageEntityModels.Image, translate: TranslateService) => this.store.select(FlatStates.PointOfInterestEntityState.imagePoiOptionArray$).pipe(map(filterFn => filterFn(image, translate)));
  poiAnnotationOptionArray$ = (poi: FlatModels.PointOfInterestEntityModels.PointOfInterest, translate: TranslateService) => this.store.select(FlatStates.AnnotationEntityState.poiAnnotationOptionArray$).pipe(map(filterFn => filterFn(poi, translate)));
  groupAnnotationOptionArray$ = (group: FlatModels.GroupEntityModels.Group, translate: TranslateService) => this.store.select(FlatStates.AnnotationEntityState.groupAnnotationOptionArray$).pipe(map(filterFn => filterFn(group, translate)));
  
  annotationLabelInfo$ = (id: string, translate: TranslateService) => this.store.select(FlatStates.AnnotationEntityState.annotationLabelInfo$).pipe(map(filterFn => filterFn(id, translate)));
  currentSelectedSlideGroupAnnotationOptions$ = (annotationOptionArray: { value: string, name: string}[], group?: FlatModels.GroupEntityModels.Group) => this.store.select(FlatStates.AnnotationGroupEntityState.currentSelectedSlideGroupAnnotationOptions$).pipe(map(filterFn => filterFn(annotationOptionArray, group)));
  currentSelectedSlideImageAnnotationOptions$ = (annotationOptionArray: { value: string, name: string}[], image?: FlatModels.ImageEntityModels.Image) => this.store.select(FlatStates.ImageEntityState.currentSelectedSlideImageAnnotationOptions$).pipe(map(filterFn => filterFn(annotationOptionArray, image)));
  currentSelectedSlideImagePoiOptions$ = (poiOptionArray: { value: string, name: string}[], image?: FlatModels.ImageEntityModels.Image) => this.store.select(FlatStates.ImageEntityState.currentSelectedSlideImagePoiOptions$).pipe(map(filterFn => filterFn(poiOptionArray, image)));
  currentSelectedSlidePoiAnnotationOptions$ = (annotationOptionArray: { value: string, name: string}[], poi?: FlatModels.PointOfInterestEntityModels.PointOfInterest) => this.store.select(FlatStates.PointOfInterestEntityState.currentSelectedSlidePoiAnnotationOptions$).pipe(map(filterFn => filterFn(annotationOptionArray, poi)));
  
  media$ = (id: string) => this.store.select(FlatStates.MediaEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  
  imagesByAnnotation$ = (id: string) => this.store.select(FlatStates.ImageEntityState.imagesByAnnotation$).pipe(map(filterFn => filterFn(id)));
  imageIdsByAnnotation$ = (id: string) => this.store.select(FlatStates.ImageEntityState.imageIdsByAnnotation$).pipe(map(filterFn => filterFn(id)));
  imagesByPoi$ = (id: string) => this.store.select(FlatStates.ImageEntityState.imagesByPoi$).pipe(map(filterFn => filterFn(id)));
  imageIdsByPoi$ = (id: string) => this.store.select(FlatStates.ImageEntityState.imageIdsByPoi$).pipe(map(filterFn => filterFn(id)));
  poisByAnnotation$ = (id: string) => this.store.select(FlatStates.PointOfInterestEntityState.poisByAnnotation$).pipe(map(filterFn => filterFn(id)));
  poiIdsByAnnotation$ = (id: string) => this.store.select(FlatStates.PointOfInterestEntityState.poiIdsByAnnotation$).pipe(map(filterFn => filterFn(id)));
  groupsByAnnotation$ = (id: string) => this.store.select(FlatStates.AnnotationGroupEntityState.groupsByAnnotation$).pipe(map(filterFn => filterFn(id)));
  groupIdsByAnnotation$ = (id: string) => this.store.select(FlatStates.AnnotationGroupEntityState.groupIdsByAnnotation$).pipe(map(filterFn => filterFn(id)));
  
  isPoiInCurrentSelectedSlideImages$ = (poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) => this.store.select(FlatStates.ImageEntityState.isPoiInCurrentSelectedSlideImages$).pipe(map(filterFn => filterFn(poi)));
  isAnnotationInCurrentSelectedSlideImages$ = (annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) => this.store.select(FlatStates.ImageEntityState.isAnnotationInCurrentSelectedSlideImages$).pipe(map(filterFn => filterFn(annotation)));
  isAnnotationInCurrentSelectedSlidePois$ = (annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) => this.store.select(FlatStates.PointOfInterestEntityState.isAnnotationInCurrentSelectedSlidePois$).pipe(map(filterFn => filterFn(annotation)));
  isAnnotationInCurrentSelectedSlideGroups$ = (annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) => this.store.select(FlatStates.AnnotationGroupEntityState.isAnnotationInCurrentSelectedSlideGroups$).pipe(map(filterFn => filterFn(annotation)));
  
  hasBulkEditLayer$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditLayer$).pipe(map(filterFn => filterFn(id)));
  isLayerCurrent$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isLayerCurrent$).pipe(map(filterFn => filterFn(id)));
  isLayerSelected$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isLayerSelected$).pipe(map(filterFn => filterFn(id)));
  
  hasBulkEditImage$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditImage$).pipe(map(filterFn => filterFn(id)));
  isImageCurrent$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isImageCurrent$).pipe(map(filterFn => filterFn(id)));
  isImageSelected$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isImageSelected$).pipe(map(filterFn => filterFn(id)));
  
  hasBulkEditGroup$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditGroup$).pipe(map(filterFn => filterFn(id)));
  isGroupSelected$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isGroupSelected$).pipe(map(filterFn => filterFn(id)));
  
  hasBulkEditAnnotation$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditAnnotation$).pipe(map(filterFn => filterFn(id)));
  isAnnotationSelected$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isAnnotationSelected$).pipe(map(filterFn => filterFn(id)));
  
  hasBulkEditQuestion$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditQuestion$).pipe(map(filterFn => filterFn(id)));
  isQuestionSelected$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isQuestionSelected$).pipe(map(filterFn => filterFn(id)));
  
  hasBulkEditPoi$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.hasBulkEditPoi$).pipe(map(filterFn => filterFn(id)));
  isPoiSelected$ = (id: string) => this.store.select(FlatStates.BulkEditAuthorState.isPoiSelected$).pipe(map(filterFn => filterFn(id)));


  slideUrlByNode$ = (slideNode: FlatModels.NodeEntityModels.SlideNode) => this.store.select(FlatStates.SlideAuthorState.slideUrlByNode$).pipe(map(filterFn => filterFn(slideNode)));

  /*
  annotation = (id: string) => this.store.select(FlatStates.AnnotationEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  annotationLabel = (id: string) => this.store.select(FlatStates.AnnotationLabelEntityState.entity$).pipe(map(filterFn => filterFn(id)));
  */

  //</editor-fold>

  //<editor-fold desc="Snapshot Selectors">
  
  snapshot_theme = () => this.store.selectSnapshot(FlatStates.SlideAuthorState.theme$);
  snapshot_chapterPanelData = () => this.store.selectSnapshot(FlatStates.SlideAuthorState.chapterPanelData$);
  snapshot_currentSelectedNode = () => this.store.selectSnapshot(FlatStates.NodeEntityState.currentSelectedNode$);
  snapshot_currentSelectedSlideAnnotation = () => this.store.selectSnapshot(FlatStates.AnnotationEntityState.currentSelectedSlideAnnotation$);
  snapshot_layerIndex = () => this.store.selectSnapshot(FlatStates.SlideLayerEntityState.layerIndex$);
  snapshot_imageIndex = () => this.store.selectSnapshot(FlatStates.ImageEntityState.imageIndex$);
  snapshot_poiId = () => this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.poiId$);
  snapshot_groupId = () => this.store.selectSnapshot(FlatStates.AnnotationGroupEntityState.groupId$);
	snapshot_editPanelDisplay = () => this.store.selectSnapshot(FlatStates.SlideAuthorState.editPanelDisplay$);
	snapshot_editPanelOverlay = () => this.store.selectSnapshot(FlatStates.SlideAuthorState.editPanelOverlay$);
	snapshot_mainPanelDisplay = () => this.store.selectSnapshot(FlatStates.SlideAuthorState.mainPanelDisplay$);
  
  snapshot_page = () => this.store.selectSnapshot(FlatStates.PageEntityState.currentSelectedSlidePage$);
  snapshot_layer = () => this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$);
  snapshot_image = () => this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$);
  snapshot_poi = () => this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.currentSelectedSlidePoi$);

  //</editor-fold>
  
  //<editor-fold desc="Actions">
  
  ofActionDispatchedDisableEditors = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.DisableEditors));
  ofActionDispatchedAddMarkerWithPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.AddMarkerWithPosition));
  ofActionDispatchedUpdateMarkerAddTargetWithPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.UpdateMarkerAddTargetWithPosition));
  ofActionDispatchedAddFreeFormWithPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.AddFreeFormWithPosition));
  ofActionDispatchedAddKeymapWithPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.AddKeymapWithPosition));
  ofActionDispatchedAddTextWithPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.AddTextWithPosition));
  ofActionDispatchedUpdateAnnotationPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.UpdateAnnotationPosition));
  ofActionDispatchedCopyAnnotationWithPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.CopyAnnotationWithPosition));
  ofActionDispatchedScrollNodeIntoView = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.ScrollNodeTreeElementIntoView));
  ofActionDispatchedScrollSlideElementIntoView = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView));
  ofActionDispatchedActivateDrawTool = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.ActivateDrawTool));
  ofActionDispatchedGoHome = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.GoHome));
  ofActionDispatchedGoToAnnotation = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.GoToAnnotation));
  ofActionDispatchedGoToPoi = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.GoToPoi));
  ofActionDispatchedAddPoiWithPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.AddPoiWithPosition));
  ofActionDispatchedUpdatePoiPosition = this.actions$.pipe(ofActionCompleted(FlatActions.SlideAuthorActions.UpdatePoiPosition));
  
  //</editor-fold>

  //<editor-fold desc="Requests">

  requestSetTheme(theme: FlatModels.SlideAuthorModels.MedsurfTheme) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTheme(theme));
  }

  requestSetLayout(layout: 'list' | 'grid') {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetLayout(layout));
  }
  
  requestDisableEditors(id: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.DisableEditors(id));
  }

  requestGetModalities() {
    return this.store.dispatch(new FlatActions.ConstantAuthorActions.GetModalities());
  }

  requestSetMediaPanelSize(sizes: number[]) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetMediaPanelSize(sizes));
  }

  requestCollapseMedia(isMinimized: boolean, override = true) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.CollapseMedia(isMinimized, override));
  }

  requestSetChapterPanelSize(sizes: number[]) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetChapterPanelSize(sizes));
  }

  requestCollapseChapter(isMinimized: boolean, override = true) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.CollapseChapter(isMinimized, override));
  }

  requestSetLayerPanelSize(sizes: number[]) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetLayerPanelSize(sizes));
  }

  requestCollapseLayer(isMinimized: boolean, override = true) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.CollapseLayer(isMinimized, override));
  }

  requestSetWysiwygPanelSize(sizes: number[]) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetWysiwygPanelSize(sizes));
  }

  requestCollapseWysiwyg(isMinimized: boolean, override = true) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.CollapseWysiwyg(isMinimized, override));
  }

  requestSetEditPanelSize(sizes: number[]) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelSize(sizes));
  }

  requestCollapseEdit(isMinimized: boolean, override = true) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.CollapseEdit(isMinimized, override));
  }

  requestSetTypedNodeExpanded(typedNodeExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedNodeExpanded(typedNodeExpanded));
  }

  requestSetTypedPageExpanded(typedPageExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedPageExpanded(typedPageExpanded));
  }
  
  requestAddTypedSlideLayersExpanded(pageId: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.AddTypedSlideLayersExpanded(pageId));
  }

  requestSetTypedSlideLayersExpanded(typedSlideLayersExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedSlideLayersExpanded(typedSlideLayersExpanded));
  }

  requestSetTypedSlideAnnotationsExpanded(typedSlideAnnotationsExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedSlideAnnotationsExpanded(typedSlideAnnotationsExpanded));
  }

  requestSetTypedSlideGroupsExpanded(typedSlideGroupsExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedSlideGroupsExpanded(typedSlideGroupsExpanded));
  }

  requestSetTypedSlidePoisExpanded(typedSlidePoisExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedSlidePoisExpanded(typedSlidePoisExpanded));
  }

  requestSetTypedSlideQuestionsExpanded(typedSlideQuestionsExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedSlideQuestionsExpanded(typedSlideQuestionsExpanded));
  }

  requestAddTypedLayerExpanded(layerId: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.AddTypedLayerExpanded(layerId));
  }

  requestSetTypedLayerExpanded(typedLayerExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedLayerExpanded(typedLayerExpanded));
  }

  requestAddTypedImageExpanded(imageId: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.AddTypedImageExpanded(imageId));
  }

  requestSetTypedImageExpanded(typedImageExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedImageExpanded(typedImageExpanded));
  }

  requestSetTypedImageAnnotationExpanded(typedImageAnnotationExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedImageAnnotationExpanded(typedImageAnnotationExpanded));
  }

  requestSetTypedImagePoiExpanded(typedImagePoiExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedImagePoiExpanded(typedImagePoiExpanded));
  }

  requestSetTypedGroupExpanded(typedGroupExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedGroupExpanded(typedGroupExpanded));
  }

  requestSetTypedPoiExpanded(typedPoiExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedPoiExpanded(typedPoiExpanded));
  }

  requestSetTypedPoiAnnotationExpanded(typedPoiAnnotationExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedPoiAnnotationExpanded(typedPoiAnnotationExpanded));
  }

  requestSetTypedQuestionExpanded(typedQuestionExpanded: { [id: string]: boolean }) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetTypedQuestionExpanded(typedQuestionExpanded));
  }

  requestGetSlideDataByPageId(pageId: string) {
    return this.store.dispatch(new FlatActions.SlideControlActions.GetSlideDataByPageId(pageId));
  }

  requestGoHome() {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.GoHome());
  }

  requestGoToAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    // Get Data
    const currentSelectedImage = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$);
    const images = this.store.selectSnapshot(FlatStates.ImageEntityState.imagesWithAnnotation$(annotation.id));
    
    // Check Image
    let image = images[0];
    let changeImage = true;
    if (currentSelectedImage && images.map(i => i.id).includes(currentSelectedImage.id)) {
      image = currentSelectedImage;
      changeImage = false;
    }
    
    return of({}).pipe(
      mergeMap((data) => {
        if (changeImage) {
          let layers: FlatModels.LayerEntityModels.Layer[] = [];
          if (images.length > 0) {
            layers = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.layersWithImage$(image.id));
          }
          
          const currentSelectedSlideLayersMap = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayersMap$);
          let layerIndex = layers.length > 0 ? currentSelectedSlideLayersMap.indexOf(layers[0].id) : 0;
          if (!Number.isInteger(layerIndex) || layerIndex < 0) {
            layerIndex = 0
          }
          return this.store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layerIndex));
        }
        return of(data);
      }),
      mergeMap((data) => {
        if (changeImage) {
          const currentSelectedSlideImagesMap = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImagesMap$);
          let imageIndex = images.length > 0 ? currentSelectedSlideImagesMap.indexOf(image.id) : 0;
          if (!Number.isInteger(imageIndex) || imageIndex < 0) {
            imageIndex = 0
          }
          return this.store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(imageIndex));
        }
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.SlideAuthorActions.GoToAnnotation(annotation))
      ),
    );
  }

  requestGoToPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.GoToPoi(poi));
  }

  requestSetEditPanelOverlay(editPanelOverlay: boolean) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelOverlay(editPanelOverlay));
  }

  requestHideEditPanel() {
    return this.store.dispatch(new FlatActions.BulkEditAuthorActions.ResetSelection()).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.list))
      ),
    );
  }

  //<editor-fold desc="Layer">

  requestSetLayerIndex(layerIndex: number) {
    const layerCount = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayers$).length || 0;
    if (0 <= layerIndex && layerIndex <= (layerCount - 1)) {
      return this.store.dispatch(new FlatActions.BulkEditAuthorActions.ResetSelection()).pipe(
        mergeMap(() =>
          this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(null))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(null))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(null))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(null))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(0))
        ),
        mergeMap(() =>
          this.store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layerIndex))
        ),
        mergeMap(() =>
          this.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.layer)
        )
      );
    }
    throw new FlatModels.ErrorControlModels.MedsurfError(
      "notification.layer_not_found",
      FlatModels.ErrorControlModels.ErrorLevel.enum.error,
      FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
      "LIBS.FlatFacades.SlideAuthorFacade.requestSetLayerIndex",
    );
  }

  requestRearrangeLayers(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                         layers: FlatModels.LayerEntityModels.Layer[]) {
    // Rearrange Nodes
    return this.store.dispatch(new FlatActions.SlideLayerEntityActions.RearrangeLayers(layers)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.page_rearrange_layers',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddLayer(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                  name: string) {
    // Add Layer
    return FlatFacades.LayerEntityFacade.addLayer(this.store, page).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateLayer(layer: FlatModels.LayerEntityModels.Layer,
                     name: string) {
    // Update Layer
    return this.store.dispatch(new FlatActions.SlideLayerEntityActions.UpdateEntity(layer)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveLayer(layer: FlatModels.LayerEntityModels.Layer,
                     name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.metadata)).pipe(
      mergeMap(() =>
        FlatFacades.LayerEntityFacade.removeLayer(this.store, layer.id)
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
      mergeMap(() =>
        this.requestSetImageIndex(0, 0)
      )
    );
  }

  //</editor-fold>

  //<editor-fold desc="Image">

  requestSetImageIndex(layerIndex: number,
                       imageIndex: number,
                       changeEditPanel = true) {
    return of({}).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.BulkEditAuthorActions.ResetSelection())
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(null))
      ),
      mergeMap((data) => {
        const currentLayerIndex = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.layerIndex$);
        if (currentLayerIndex !== layerIndex) {
          return this.store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layerIndex))
        }
        return of(data);
      }),
      mergeMap((data) => {
        const imageCount = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideValidImages$).length || 0;
        if (0 <= imageIndex && imageIndex <= (imageCount - 1)) {
          return this.store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(imageIndex)).pipe(
            mergeMap((data) => {
              if (changeEditPanel) {
                return this.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image)
              }
              return of(data);
            }),
          );
        }
        return of(data);
      })
    )
  }

  requestRearrangeImages(layer: FlatModels.LayerEntityModels.Layer,
                         images: FlatModels.ImageEntityModels.Image[]) {
    // Rearrange Images
    return this.store.dispatch(new FlatActions.ImageEntityActions.RearrangeImages(images)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.layer_rearrange_images',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddImage(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                  layer: FlatModels.LayerEntityModels.Layer,
                  name: string) {
    // Add Image
    return FlatFacades.ImageEntityFacade.addImage(this.store, page, layer).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddImageAndThumbnail(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                              layer: FlatModels.LayerEntityModels.Layer,
                              mediaId: string) {
    return of({}).pipe(
      mergeMap(() =>
        FlatFacades.ImageEntityFacade.addImage(this.store, page, layer)
      ),
      mergeMap(() => {
        const currentSelectedSlideImage = structuredClone(this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$));
        if (currentSelectedSlideImage === null) {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.image_not_found',
            FlatModels.ErrorControlModels.ErrorLevel.enum.error,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatFacades.SlideAuthorFacade.requestAddImageAndThumbnail"
          );
        }
        return this.requestUpdateImageAndThumbnail(page, currentSelectedSlideImage, mediaId, 'history.image_add')
      }),
    );
  }

  requestAddLayerAndImageAndThumbnail(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                                      mediaId: string) {
    // Get Data
    let currentSelectedSlideLayer = structuredClone(this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$));
    let currentSelectedSlideImage = structuredClone(this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$));
    
    return of({}).pipe(
      mergeMap((result) => {
        if (!currentSelectedSlideLayer) {
          return FlatFacades.LayerEntityFacade.addLayer(this.store, page);
        }
        return of(result);
      }),
      mergeMap((result) => {
        currentSelectedSlideLayer = structuredClone(this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayer$));
        if (!currentSelectedSlideImage) {
          return FlatFacades.ImageEntityFacade.addImage(this.store, page, currentSelectedSlideLayer)
        }
        return of(result);
      }),
      mergeMap(() => {
        currentSelectedSlideImage = structuredClone(this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$));
        if (currentSelectedSlideImage === null) {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.image_not_found',
            FlatModels.ErrorControlModels.ErrorLevel.enum.error,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatFacades.SlideAuthorFacade.requestAddLayerAndImageAndThumbnail"
          );
        }
        return this.requestUpdateImageAndThumbnail(page, currentSelectedSlideImage, mediaId, 'history.image_add');
      }),
    );
  }

  requestUpdateImageAndThumbnail(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                                 image: FlatModels.ImageEntityModels.Image,
                                 mediaId: string | null,
                                 name: string) {
    // Get Data
    const typedLayers = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
    const typedMedias = this.store.selectSnapshot(FlatStates.MediaEntityState.typedEntities$);
    
    let imageCount = 0;
    for (const layerId of page.layers) {
      const layer = typedLayers[layerId];
      if (layer) {
        imageCount += layer.images.length;
      }
      if (imageCount >= 2) {
        break;
      }
    }
    
    // Update Data
    if (!page.thumbnailMedia || imageCount < 2) {
      page.thumbnailMedia = mediaId;
      page.mediaType = mediaId ? typedMedias[mediaId].type : null;
    }
    image.media = mediaId;
    
    // Update Page And Image
    return this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      ),
      mergeMap(() => this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateImage(image: FlatModels.ImageEntityModels.Image,
                     name: string) {
    // Update Image
    return this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image)).pipe(
      mergeMap(() => this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveImage(image: FlatModels.ImageEntityModels.Image,
                     name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.layer)).pipe(
      mergeMap(() =>
        FlatFacades.ImageEntityFacade.removeImage(this.store, image.id)
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
      mergeMap(() =>
        this.requestSetImageIndex(0, 0)
      )
    );
  }

  requestAddPoiIdToImage(image: FlatModels.ImageEntityModels.Image,
                         poiId: string,
                         name: string) {
    // Get Data
    const typedPois = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    
    // Get Poi
    const poi = structuredClone(typedPois[poiId]);
    if (poi.images) {
      if (poi.images.findIndex(i => i === image.id) === -1) {
        poi.images.push(image.id);
      }
    } else {
      poi.images = [image.id];
    }
    
    // Update Image
    image.pois.push(poiId);
    
    // Update Image
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestAddPoiIdsToImage(image: FlatModels.ImageEntityModels.Image,
                          poiIds: string[],
                          name: string) {
    // Get Data
    const typedPois = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    
    // Get Pois
    const pois = [];
    for (const poiId of poiIds) {
      // Get Poi
      const poi = structuredClone(typedPois[poiId]);
      if (poi.images) {
        if (poi.images.findIndex(i => i === image.id) === -1) {
          poi.images.push(image.id);
        }
      } else {
        poi.images = [image.id];
      }
      pois.push(poi);
      
      // Update Image
      image.pois.push(poiId);
    }
    
    // Update Image
    return forkJoin(pois.map(poi => this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestAddPoiIdToImages(imageIds: string[],
                          poiId: string,
                          name: string) {
    // Get Data
    const typedPois = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    const typedImages = this.store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    
    // Update Images
    const pois: FlatModels.PointOfInterestEntityModels.PointOfInterest[] = [];
    const images: FlatModels.ImageEntityModels.Image[] = [];
    for (const imageId of imageIds) {
      // Get Poi
      const poi = structuredClone(typedPois[poiId]);
      if (poi.images) {
        if (poi.images.findIndex(i => i === imageId) === -1) {
          poi.images.push(imageId);
        }
      } else {
        poi.images = [imageId];
      }
      pois.push(poi);
      
      // Update Image
      const image = structuredClone(typedImages[imageId]);
      image.pois.push(poiId);
      images.push(image);
    }
    
    // Update Images
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(pois.map(poi => this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi))))
      ),
      mergeMap(() =>
        forkJoin(images.map(image => this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemovePoiFromImage(image: FlatModels.ImageEntityModels.Image,
                            poiId: string,
                            name: string) {
    // Get Data
    const typedPois = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    
    // Get Poi
    const poi = structuredClone(typedPois[poiId]);
    if (poi.images) {
      poi.images.splice(poi.images.findIndex(i => i === image.id), 1);
    } else {
      poi.images = [];
    }
    
    // Update Image
    image.pois.splice(image.pois.findIndex(a => a === poiId), 1);
    
    // Update Image
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemovePoiFromImages(imageIds: string[],
                             poiId: string,
                             name: string) {
    // Get Data
    const typedPois = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    const typedImages = this.store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    
    // Update Images
    const pois: FlatModels.PointOfInterestEntityModels.PointOfInterest[] = [];
    const images: FlatModels.ImageEntityModels.Image[] = [];
    for (const imageId of imageIds) {
      // Get Poi
      const poi = structuredClone(typedPois[poiId]);
      if (poi.images) {
        if (poi.images.findIndex(i => i === imageId) === -1) {
          poi.images.splice(poi.images.findIndex(i => i === imageId), 1);
        }
      } else {
        poi.images = [imageId];
      }
      pois.push(poi);
      
      // Update Image
      const image = structuredClone(typedImages[imageId]);
      image.pois.splice(image.pois.findIndex((id: string) => id === poiId), 1);
      images.push(image);
    }
    
    // Update Images
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(pois.map(poi => this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi))))
      ),
      mergeMap(() =>
        forkJoin(images.map(image => this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddAnnotationIdToImage(image: FlatModels.ImageEntityModels.Image,
                                annotationId: string,
                                name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Annotation
    const annotation = structuredClone(typedAnnotations[annotationId]);
    if (annotation.images) {
      if (annotation.images.findIndex(i => i === image.id) === -1) {
        annotation.images.push(image.id);
      }
    } else {
      annotation.images = [image.id];
    }
    
    // Update Image
    image.annotations.push(annotationId);
    
    // Update Image
    return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestAddAnnotationIdsToImage(image: FlatModels.ImageEntityModels.Image,
                                 annotationIds: string[],
                                 name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Annotations
    const annotations = [];
    for (const annotationId of annotationIds) {
      // Get Annotation
      const annotation = structuredClone(typedAnnotations[annotationId]);
      if (annotation.images) {
        if (annotation.images.findIndex(i => i === image.id) === -1) {
          annotation.images.push(image.id);
        }
      } else {
        annotation.images = [image.id];
      }
      annotations.push(annotation);
      
      // Update Image
      image.annotations.push(annotationId);
    }
    
    // Update Image
    return forkJoin(annotations.map(annotation => this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation)))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestAddAnnotationIdToImages(imageIds: string[],
                                 annotationId: string,
                                 name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    const typedImages = this.store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    
    // Update Images
    const annotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[] = [];
    const images: FlatModels.ImageEntityModels.Image[] = [];
    for (const imageId of imageIds) {
      // Get Annotation
      const annotation = structuredClone(typedAnnotations[annotationId]);
      if (annotation.images) {
        if (annotation.images.findIndex(i => i === imageId)) {
          annotation.images.push(imageId);
        }
      } else {
        annotation.images = [imageId];
      }
      annotations.push(annotation);
      
      // Update Image
      const image = structuredClone(typedImages[imageId]);
      image.annotations.push(annotationId);
      images.push(image);
    }
    
    // Update Images
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(annotations.map(annotation => this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation))))
      ),
      mergeMap(() =>
        forkJoin(images.map(image => this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestRemoveAnnotationFromImage(image: FlatModels.ImageEntityModels.Image,
                                   annotationId: string,
                                   name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Annotation
    const annotation = structuredClone(typedAnnotations[annotationId]);
    if (annotation.images) {
      annotation.images.splice(annotation.images.findIndex(i => i === image.id), 1);
    } else {
      annotation.images = [];
    }
    
    // Update Image
    image.annotations.splice(image.annotations.findIndex(a => a === annotationId), 1);
    
    // Update Group
    return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveAnnotationFromImages(imageIds: string[],
                                    annotationId: string,
                                    name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    const typedImages = this.store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    
    // Update Images
    const annotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[] = [];
    const images: FlatModels.ImageEntityModels.Image[] = [];
    for (const imageId of imageIds) {
      // Get Annotation
      const annotation = structuredClone(typedAnnotations[annotationId]);
      if (annotation.images) {
        annotation.images.splice(annotation.images.findIndex((id: string) => id === imageId), 1);
      } else {
        annotation.images = [];
      }
      annotations.push(annotation);
      
      // Update Image
      const image = structuredClone(typedImages[imageId]);
      image.annotations.splice(image.annotations.findIndex((id: string) => id === annotationId), 1);
      images.push(image);
    }
    
    // Update Images
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(annotations.map(annotation => this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation))))
      ),
      mergeMap(() =>
        forkJoin(images.map(image => this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestSetStartImage(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                       defaultImage: FlatModels.ImageEntityModels.Image) {
    // Get Data
    const typedLayers = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
    const typedImages = this.store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
    const typedMedias = this.store.selectSnapshot(FlatStates.MediaEntityState.typedEntities$);
    
    // Get Old Default Image
    let alreadySelected = false;
    const changeImages = page.layers.reduce((arr, layer) => {
      const layerEntity = typedLayers[layer];
      if (layerEntity) {
        layerEntity.images.map(image => {
          const imageEntity = structuredClone(typedImages[image]);
          if (imageEntity && imageEntity.default) {
            if (imageEntity.id === defaultImage.id) {
              alreadySelected = true;
            }
            imageEntity.default = false;
            arr.push(imageEntity);
          }
        })
      }
      return arr;
    }, [] as FlatModels.ImageEntityModels.Image[]);
    
    if (alreadySelected) {
      return of({});
    }
    
    // Update Default Image
    defaultImage.default = true;
    changeImages.push(defaultImage);
    
    // Update Page Media Type
    const updatePage = structuredClone(page);
    let mediaTypeChanged = false;
    if (defaultImage && defaultImage.media) {
      // Get Media
      const media = typedMedias[defaultImage.media];
      
      if (updatePage.mediaType !== media.type) {
        mediaTypeChanged = true;
        
        // Update Page
        updatePage.mediaType = media.type;
      }
    }
    
    // Update Images
    return of(changeImages).pipe(
      mergeMap((images) =>
        forkJoin(images.map(image => this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))))
      ),
      mergeMap((data) => {
        if (mediaTypeChanged) {
          return this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(updatePage));
        }
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.image_change_default',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  //</editor-fold>

  //<editor-fold desc="Question">

  requestSetQuestionId(questionId: string | null,
                       editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay) {
    return this.store.dispatch(new FlatActions.BulkEditAuthorActions.ResetSelection()).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(questionId))
      ),
      mergeMap(() => {
        if (questionId === null) {
          return this.requestSetEditPanelDisplay(editPanelDisplay);
        }
        return this.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question);
      })
    );
  }

  requestRearrangeQuestions(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                            questions: FlatModels.QuestionEntityModels.QuestionEntityType[]) {
    // Rearrange Questions
    return this.store.dispatch(new FlatActions.QuestionEntityActions.RearrangeQuestions(questions)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.page_rearrange_questions',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                     type: FlatModels.QuestionEntityModels.QuestionType) {
    switch (type) {
      case FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion:
        return this.requestAddSingleChoiceQuestion(page);
      case FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion:
        return this.requestAddLongListQuestion(page);
      case FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion:
        return this.requestAddMappingQuestion(page);
      case FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion:
        return this.requestAddKPrimeQuestion(page);
      case FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion:
        return this.requestAddTrueFalseQuestion(page);
      case FlatModels.QuestionEntityModels.QuestionType.enum.FreeTextQuestion:
        return this.requestAddFreeTextQuestion(page);
      case FlatModels.QuestionEntityModels.QuestionType.enum.IndicationQuestion:
        return this.requestAddIndicationQuestion(page);
      case FlatModels.QuestionEntityModels.QuestionType.enum.MultipleChoiceQuestion:
        return this.requestAddMultipleChoiceQuestion(page);
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.type_not_supported',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatFacades.SlideAuthorFacade.requestAddQuestion"
        );
    }
    /*
    // Create New Question
    const newQuestion: FlatModels.QuestionEntityModels.SingleChoiceQuestion = {
      id: uuid(),
      order: page.questions?.length || 0,
      choices: [],
      content: 'Neue Frage',
      placeholder: null,
      explanation: null,
      required: false,
      randomised: false,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion,
      solution: null
    };

    // Update Page
    page.questions.push(newQuestion.id);

    // Add Question And Update Page
    return this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newQuestion)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
    */
  }

  requestAddSingleChoiceQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    const questionId = uuid();
    
    // Create New Choices
    const newChoice1: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 0,
      question: questionId,
    };
    const newChoice2: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 1,
      question: questionId,
    };
    const newChoice3: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 2,
      question: questionId,
    };
    const newChoice4: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 3,
      question: questionId,
    };
    const newChoice5: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 4,
      question: questionId,
    };

    // Create New Single Choice Question
    const newSingleChoiceQuestion: FlatModels.QuestionEntityModels.SingleChoiceQuestion = {
      id: questionId,
      order: page.questions?.length || 0,
      choices: [newChoice1.id, newChoice2.id, newChoice3.id, newChoice4.id, newChoice5.id],
      content: 'Neue Frage',
      placeholder: null,
      explanation: 'Erklärung',
      required: false,
      randomised: false,
      customEnumeration: null,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion,
      solution: 0,
      slide: page.id,
    };

    // Update Page
    page.questions.push(newSingleChoiceQuestion.id);

    // Add Choices And Question And Update Page
    return this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice1)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice2))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice3))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice4))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice5))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newSingleChoiceQuestion))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(newSingleChoiceQuestion.id));
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newSingleChoiceQuestion.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_add_single_choice',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddLongListQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    const questionId = uuid();
    
    // Create New Choices
    const newChoice1: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 0,
      question: questionId,
    };
    // Changes from Daniela to merge LongList And SingleChoice
    const newChoice2: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 1,
      question: questionId,
    };
    const newChoice3: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 2,
      question: questionId,
    };
    const newChoice4: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 3,
      question: questionId,
    };
    const newChoice5: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 4,
      question: questionId,
    };

    // Create New Long List Question
    const newLongListQuestion: FlatModels.QuestionEntityModels.LongListQuestion = {
      id: questionId,
      order: page.questions?.length || 0,
      // // Changes from Daniela to merge LongList And SingleChoice: choices: [newChoice1.id],
      choices: [newChoice1.id, newChoice2.id, newChoice3.id, newChoice4.id, newChoice5.id],
      content: 'Neue Frage',
      placeholder: null,
      explanation: 'Erklärung',
      required: false,
      randomised: false,
      customEnumeration: null,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion,
      solution: 0,
      slide: page.id,
    };

    // Update Page
    page.questions.push(newLongListQuestion.id);

    // Add Choices And Question And Update Page
    return this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice1)).pipe(
      // Changes from Daniela to merge LongList And SingleChoice
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice2))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice3))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice4))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice5))
      ),
      // Changes from Daniela to merge LongList And SingleChoice
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newLongListQuestion))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(newLongListQuestion.id));
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newLongListQuestion.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_add_long_list',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddMappingQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    const questionId = uuid();
    
    // Create New Choices
    const newChoice1: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 0,
      question: questionId,
    };
    const newChoice2: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 1,
      question: questionId,
    };
    const newChoice3: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 2,
      question: questionId,
    };
    const newChoice4: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 3,
      question: questionId,
    };
    const newChoice5: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 4,
      question: questionId,
    };

    // Create New Mapping Question
    const newMappingQuestion: FlatModels.QuestionEntityModels.MappingQuestion = {
      id: questionId,
      order: page.questions?.length || 0,
      choices: [newChoice1.id, newChoice2.id, newChoice3.id, newChoice4.id, newChoice5.id],
      content: 'Neue Frage',
      placeholder: null,
      explanation: 'Erklärung',
      required: false,
      randomised: false,
      customEnumeration: null,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion,
      solutions: ['0'],
      slide: page.id,
    };

    // Update Page
    page.questions.push(newMappingQuestion.id);

    // Add Choices And Question And Update Page
    return this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice1)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice2))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice3))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice4))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice5))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newMappingQuestion))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(newMappingQuestion.id));
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newMappingQuestion.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_add_mapping',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddKPrimeQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    const questionId = uuid();
    
    // Create New Choices
    const newChoice1: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 0,
      question: questionId,
    };
    const newChoice2: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 1,
      question: questionId,
    };
    const newChoice3: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 2,
      question: questionId,
    };
    const newChoice4: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 3,
      question: questionId,
    };

    // Create New KPrime Question
    const newKPrimeQuestion: FlatModels.QuestionEntityModels.KprimeQuestion = {
      id: questionId,
      order: page.questions?.length || 0,
      choices: [newChoice1.id, newChoice2.id, newChoice3.id, newChoice4.id],
      content: 'Neue Frage',
      placeholder: null,
      explanation: 'Erklärung',
      required: false,
      randomised: false,
      customEnumeration: null,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion,
      solutions: ['0', '0', '0', '0'],
      slide: page.id,
    };

    // Update Page
    page.questions.push(newKPrimeQuestion.id);

    // Add Choices And Question And Update Page
    return this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice1)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice2))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice3))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice4))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newKPrimeQuestion))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(newKPrimeQuestion.id));
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newKPrimeQuestion.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_add_kprime',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddTrueFalseQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    const questionId = uuid();
    
    // Create New Choices
    const newChoice1: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 0,
      question: questionId,
    };
    // Changes from Daniela to merge LongList And SingleChoice
    const newChoice2: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 1,
      question: questionId,
    };
    const newChoice3: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 2,
      question: questionId,
    };
    const newChoice4: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      label: '',
      value: null,
      order: 3,
      question: questionId,
    };

    // Create New True False Question
    const newTrueFalseQuestion: FlatModels.QuestionEntityModels.TrueFalseQuestion = {
      id: questionId,
      order: page.questions?.length || 0,
      // Changes from Daniela to merge LongList And SingleChoice: choices: [newChoice1.id],
      choices: [newChoice1.id, newChoice2.id, newChoice3.id, newChoice4.id],
      content: 'Neue Frage',
      placeholder: null,
      explanation: 'Erklärung',
      required: false,
      randomised: false,
      customEnumeration: null,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion,
      solutions: ['0', '0', '0', '0'],
      slide: page.id,
    };

    // Update Page
    page.questions.push(newTrueFalseQuestion.id);

    // Add Choices And Question And Update Page
    return this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice1)).pipe(
      // Changes from Daniela to merge LongList And SingleChoice
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice2))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice3))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice4))
      ),
      // Changes from Daniela to merge LongList And SingleChoice
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newTrueFalseQuestion))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(newTrueFalseQuestion.id));
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newTrueFalseQuestion.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_add_true_false',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddFreeTextQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    // Create New FreeText Question
    const newFreeTextQuestion: FlatModels.QuestionEntityModels.FreeTextQuestion = {
      id: uuid(),
      order: page.questions?.length || 0,
      choices: [],
      content: 'Neue Frage',
      placeholder: null,
      explanation: 'Erklärung',
      required: false,
      randomised: false,
      customEnumeration: null,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.FreeTextQuestion,
      solutionText: 'Antwort',
      slide: page.id,
    };

    // Update Page
    page.questions.push(newFreeTextQuestion.id);

    // Add Choices And Question And Update Page
    return this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newFreeTextQuestion)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(newFreeTextQuestion.id));
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newFreeTextQuestion.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_add_free_text',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddIndicationQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    // Create New Indication Question
    const newIndicationQuestion: FlatModels.QuestionEntityModels.IndicationQuestion = {
      id: uuid(),
      order: page.questions?.length || 0,
      choices: [],
      content: 'Neue Frage',
      placeholder: null,
      explanation: 'Erklärung',
      required: false,
      randomised: false,
      customEnumeration: null,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.IndicationQuestion,
      solution: 0,
      slide: page.id,
    };

    // Update Page
    page.questions.push(newIndicationQuestion.id);

    // Add Choices And Question And Update Page
    return this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newIndicationQuestion)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(newIndicationQuestion.id));
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newIndicationQuestion.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_add_indication',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddMultipleChoiceQuestion(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training) {
    // Create New Multiple Choice Question
    const newMultipleChoiceQuestion: FlatModels.QuestionEntityModels.MultipleChoiceQuestion = {
      id: uuid(),
      order: page.questions?.length || 0,
      choices: [],
      content: 'Neue Frage',
      placeholder: null,
      explanation: 'Erklärung',
      required: false,
      randomised: false,
      customEnumeration: null,
      numberingType: FlatModels.QuestionEntityModels.NumberingType.LETTER,
      type: FlatModels.QuestionEntityModels.QuestionType.enum.MultipleChoiceQuestion,
      solutions: [],
      slide: page.id
    };

    // Update Page
    page.questions.push(newMultipleChoiceQuestion.id);

    // Add Choices And Question And Update Page
    return this.store.dispatch(new FlatActions.QuestionEntityActions.AddEntity(newMultipleChoiceQuestion)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(newMultipleChoiceQuestion.id));
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.question));
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newMultipleChoiceQuestion.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_add_multiple_choice',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateQuestion(question: FlatModels.QuestionEntityModels.QuestionEntityType,
                        name: string) {
    // Update Question
    return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestChangeQuestionType(question: FlatModels.QuestionEntityModels.QuestionEntityType,
                            type: FlatModels.QuestionEntityModels.QuestionType) {
    // Check Type
    let name = '';
    let typedEntities: { [id: string]: FlatModels.ChoiceEntityModels.Choice };
    let choices: FlatModels.ChoiceEntityModels.Choice[];
    switch (type) {
      case FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion: {
        name = 'history.question_update_type_to_single_choice';
        const single_choice_max_choices = 5;
        
        // Update Question
        if (question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion
          && question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion) {
          (question as any).solution = 0;
        }
        delete (question as any)['solutions'];
        delete (question as any)['solutionText'];
        question.type = type;
        
        // Check Choices
        if ((question.choices || []).length < single_choice_max_choices) {
          // Add Choices
          const choicesList = question.choices || [];
          const choices: FlatModels.ChoiceEntityModels.Choice[] = [];
          for (let i = (question.choices || []).length; i < single_choice_max_choices; i++) {
            const newChoice: FlatModels.ChoiceEntityModels.Choice = {
              id: uuid(),
              order: i,
              label: '',
              value: null,
              question: question.id
            }
            choices.push(newChoice);
            choicesList.push(newChoice.id);
          }
          
          // Update Choices
          question.choices = choicesList;
          
          // Add Choices And Update Question
          return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
            mergeMap(() =>
              forkJoin(from(choices).pipe(map((choice) => this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(choice)))))
            ),
            mergeMap(() =>
              this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
                name,
                this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
              ))
            ),
          );
        } else if (question.choices.length > single_choice_max_choices) {
          // Remove Choices
          const choiceIds = question.choices.splice(single_choice_max_choices, Infinity);
          
          // Get Choices
          const typedEntities = this.store.selectSnapshot(FlatStates.ChoiceEntityState.typedEntities$);
          const choices = choiceIds.reduce((arr: FlatModels.ChoiceEntityModels.Choice[], id) => {
            const entity = typedEntities[id];
            if (entity) {
              arr.push(entity);
            }
            return arr;
          }, []);
          
          // Delete Choices And Update Question
          return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
            mergeMap(() =>
              forkJoin(from(choices).pipe(map((choice) => this.store.dispatch(new FlatActions.ChoiceEntityActions.RemoveEntity(choice)))))
            ),
            mergeMap(() =>
              this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
                name,
                this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
              ))
            ),
          );
        }
        break;
      }
      case FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion:
        name = 'history.question_update_type_to_long_list';

        // Update Question
        if (question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion
            && question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion) {
          (question as any).solution = 0;
        }
        delete (question as any)['solutions'];
        delete (question as any)['solutionText'];
        question.type = type;

        break;
      case FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion:
        name = 'history.question_update_type_to_mapping';

        // Update Question
        if (question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.MappingQuestion) {
          (question as any).solutions = ['0'];
        }
        delete (question as any)['solution'];
        delete (question as any)['solutionText'];
        question.type = type;

        break;
      case FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion: {
        name = 'history.question_update_type_to_kprime';
        const kprim_max_choices = 4;
        
        // Update Question
        delete (question as any)['solution'];
        delete (question as any)['solutionText'];
        question.type = type;
        
        // Check Solutions
        if (((question as any).solutions || []).length < kprim_max_choices) {
          // Add Solutions
          for (let i = ((question as any).solutions || []).length; i < kprim_max_choices; i++) {
            if (!Array.isArray((question as any).solutions)) {
              (question as any).solutions = [];
            }
            (question as any).solutions.push('0');
          }
        } else if ((question as any).solutions.length > kprim_max_choices) {
          // Remove Solutions
          (question as any).solutions.splice(kprim_max_choices, Infinity);
        }
        
        // Check Choices
        if ((question.choices || []).length < kprim_max_choices) {
          // Add Choices
          const choicesList = question.choices || [];
          const choices: FlatModels.ChoiceEntityModels.Choice[] = [];
          for (let i = (question.choices || []).length; i < kprim_max_choices; i++) {
            const newChoice: FlatModels.ChoiceEntityModels.Choice = {
              id: uuid(),
              order: i,
              label: '',
              value: null,
              question: question.id
            }
            choices.push(newChoice);
            choicesList.push(newChoice.id);
          }
          
          // Update Choices
          question.choices = choicesList;
          
          // Add Choices And Update Question
          return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
            mergeMap(() =>
              forkJoin(from(choices).pipe(map((choice) => this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(choice)))))
            ),
            mergeMap(() =>
              this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
                name,
                this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
              ))
            ),
          );
        } else if (question.choices.length > kprim_max_choices) {
          // Remove Choices
          const choiceIds = question.choices.splice(kprim_max_choices, Infinity);
          
          // Get Choices
          typedEntities = this.store.selectSnapshot(FlatStates.ChoiceEntityState.typedEntities$);
          choices = choiceIds.reduce((arr: FlatModels.ChoiceEntityModels.Choice[], id) => {
            const entity = typedEntities[id];
            if (entity) {
              arr.push(entity);
            }
            return arr;
          }, []);
          
          // Delete Choices And Update Question
          return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
            mergeMap(() =>
              forkJoin(from(choices).pipe(map((choice) => this.store.dispatch(new FlatActions.ChoiceEntityActions.RemoveEntity(choice)))))
            ),
            mergeMap(() =>
              this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
                name,
                this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
              ))
            ),
          );
        }
        break;
      }
      case FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion:
        name = 'history.question_update_type_to_true_false';

        // Update Question
        if (question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion
            && question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion) {
          (question as any).solutions = ['0'];
        }
        delete (question as any)['solution'];
        delete (question as any)['solutionText'];
        question.type = type;

        break;
      case FlatModels.QuestionEntityModels.QuestionType.enum.FreeTextQuestion:
        name = 'history.question_update_type_to_free_text';

        // Update Question
        if (question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.FreeTextQuestion) {
          (question as any).solutionText = null;
        }
        delete (question as any)['solution'];
        delete (question as any)['solutions'];
        question.type = type;

        // Get Choices
        typedEntities = this.store.selectSnapshot(FlatStates.ChoiceEntityState.typedEntities$);
        choices = question.choices.reduce((arr: FlatModels.ChoiceEntityModels.Choice[], id) => {
          const entity = typedEntities[id];
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, []);

        // Delete Choices And Update Question
        return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
          mergeMap(() =>
            forkJoin(from(choices).pipe(map((choice) => this.store.dispatch(new FlatActions.ChoiceEntityActions.RemoveEntity(choice)))))
          ),
          mergeMap(() => {
            return this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
              name,
              this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
            ))
          }),
        );
      case FlatModels.QuestionEntityModels.QuestionType.enum.IndicationQuestion:
        name = 'history.question_update_type_to_indication';

        // Update Question
        if (question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.SingleChoiceQuestion
            && question.type !== FlatModels.QuestionEntityModels.QuestionType.enum.LongListQuestion) {
          (question as any).solution = 0;
        }
        delete (question as any)['solutions'];
        delete (question as any)['solutionText'];
        question.type = type;

        // Get Choices
        typedEntities = this.store.selectSnapshot(FlatStates.ChoiceEntityState.typedEntities$);
        choices = question.choices.reduce((arr: FlatModels.ChoiceEntityModels.Choice[], id) => {
          const entity = typedEntities[id];
          if (entity) {
            arr.push(entity);
          }
          return arr;
        }, []);

        // Delete Choices And Update Question
        return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
          mergeMap(() =>
            forkJoin(from(choices).pipe(map((choice) => this.store.dispatch(new FlatActions.ChoiceEntityActions.RemoveEntity(choice)))))
          ),
          mergeMap(() =>
            this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
              name,
              this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
            ))
          ),
        );
      case FlatModels.QuestionEntityModels.QuestionType.enum.MultipleChoiceQuestion:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.not_implemented',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatFacades.SlideAuthorFacade.requestChangeQuestionType"
        );
      default:
        throw new FlatModels.ErrorControlModels.MedsurfError(
          'notification.type_not_supported',
          FlatModels.ErrorControlModels.ErrorLevel.enum.error,
          FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
          "LIBS.FlatFacades.SlideAuthorFacade.requestChangeQuestionType"
        );
    }

    // Update Entity
    return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveQuestion(question: FlatModels.QuestionEntityModels.QuestionEntityType,
                        name: string) {
    return FlatFacades.QuestionEntityFacade.removeQuestion(this.store, question.id).pipe(
      map(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveMappingQuestionSolution(question: FlatModels.QuestionEntityModels.MappingQuestion,
                                       index: number,
                                       name: string) {
    // Remove Solution
    question.solutions.splice(index, 1);

    // Update Question
    return this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  //</editor-fold>

  //<editor-fold desc="Choices">

  requestRearrangeChoices(question: FlatModels.QuestionEntityModels.QuestionEntityType,
                          choices: FlatModels.ChoiceEntityModels.Choice[]) {
    // Rearrange Choices
    return this.store.dispatch(new FlatActions.ChoiceEntityActions.RearrangeChoices(question, choices)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.question_rearrange_choices',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddChoice(question: FlatModels.QuestionEntityModels.QuestionEntityType,
                   name: string) {
    // Create New Choice
    const newChoice: FlatModels.ChoiceEntityModels.Choice = {
      id: uuid(),
      order: question.choices?.length || 0,
      label: '',
      value: null,
      question: question.id
    };

    // Update Question
    question.choices.push(newChoice.id);

    // Add Solution
    if (question.type === FlatModels.QuestionEntityModels.QuestionType.enum.KprimeQuestion || question.type === FlatModels.QuestionEntityModels.QuestionType.enum.TrueFalseQuestion) {
      (question as FlatModels.QuestionEntityModels.KprimeQuestion | FlatModels.QuestionEntityModels.TrueFalseQuestion).solutions.push('0');
    }

    // Add Choice And Update Question
    return this.store.dispatch(new FlatActions.ChoiceEntityActions.AddEntity(newChoice)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.UpdateEntity(question))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateChoice(choice: FlatModels.ChoiceEntityModels.Choice,
                      name: string) {
    // Update Choice
    return this.store.dispatch(new FlatActions.ChoiceEntityActions.UpdateEntity(choice)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    )
  }

  requestRemoveChoice(choice: FlatModels.ChoiceEntityModels.Choice,
                      name: string) {
    return FlatFacades.ChoiceEntityFacade.removeChoice(this.store, choice.id).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  //</editor-fold>

  //<editor-fold desc="Annotation">
  
  requestUpdateMoveMultipleAnnotations(annotations: FlatModels.ImageSharedModels.ElementModels[],
                                       name: string) {
    return of(annotations).pipe(
      mergeMap(() =>
        forkJoin(annotations.map(annotation => {
          switch (annotation.annotation.type) {
            case FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker:
            case FlatModels.AnnotationEntityModels.AnnotationType.enum.Text: {
              const markerModel = structuredClone(annotation) as FlatModels.ImageSharedModels.MarkerModel;
              return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(markerModel.annotation)).pipe(
                mergeMap((result) => {
                  if (markerModel.source) {
                    return this.store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(markerModel.source))
                  }
                  return of(result);
                }),
                mergeMap(() =>
                  forkJoin(markerModel.targets.map(target => this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(structuredClone(target)))))
                ),
              );
            }
            case FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm: {
              const freeFormModel = structuredClone(annotation) as FlatModels.ImageSharedModels.FreeformModel;
              return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(freeFormModel.annotation)).pipe(
                mergeMap((result) => {
                  if (freeFormModel.source) {
                    return this.store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(freeFormModel.source))
                  }
                  return of(result);
                }),
                mergeMap((result) => {
                  if (freeFormModel.target) {
                    return this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(freeFormModel.target))
                  }
                  return of(result);
                }),
              );
            }
            case FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap: {
              const keymapModel = structuredClone(annotation) as FlatModels.ImageSharedModels.KeymapModel;
              return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(keymapModel.annotation)).pipe(
                mergeMap((result) => {
                  if (keymapModel.source) {
                    return this.store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(keymapModel.source))
                  }
                  return of(result);
                }),
              );
            }
          }
        }))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestSetAnnotationId(annotationId: string | null,
                         editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay) {
    // Get Data
    const currentSelectedImage = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$);
    let images: FlatModels.ImageEntityModels.Image[] = [];
    if (annotationId) {
      images = this.store.selectSnapshot(FlatStates.ImageEntityState.imagesWithAnnotation$(annotationId));
    }
    
    // Check Image
    let changeImage = false;
    let image = currentSelectedImage as FlatModels.ImageEntityModels.Image;
    if (images.length > 0 && annotationId !== null) {
      if (currentSelectedImage && !images.map(i => i.id).includes(currentSelectedImage.id)) {
        changeImage = true;
        image = images[0];
      }
    }

    return of({}).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.BulkEditAuthorActions.ResetSelection())
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(null))
      ),
      mergeMap((data) => {
        if (changeImage) {
          let layers: FlatModels.LayerEntityModels.Layer[] = [];
          if (images.length > 0) {
            layers = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.layersWithImage$(image.id));
          }
          
          const currentSelectedSlideLayersMap = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayersMap$);
          let layerIndex = layers.length > 0 ? currentSelectedSlideLayersMap.indexOf(layers[0].id) : 0;
          if (!Number.isInteger(layerIndex) || layerIndex < 0) {
            layerIndex = 0
          }
          return this.store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layerIndex));
        }
        return of(data);
      }),
      mergeMap((data) => {
        if (changeImage) {
          const currentSelectedSlideImagesMap = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImagesMap$);
          let imageIndex = images.length > 0 ? currentSelectedSlideImagesMap.indexOf(image.id) : 0;
          if (!Number.isInteger(imageIndex) || imageIndex < 0) {
            imageIndex = 0
          }
          return this.store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(imageIndex));
        }
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(annotationId))
      ),
      mergeMap(() => {
        if (annotationId === null) {
          return this.requestSetEditPanelDisplay(editPanelDisplay);
        }
        return this.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.annotation)
      }),
    );
  }
 
  requestAddMarkerWithPosition(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                               image: FlatModels.ImageEntityModels.Image | null,
                               poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                               tipType: FlatModels.AnnotationEntityModels.TipType,
                               text: string | null,
                               name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.AddMarkerWithPosition(page, image, poi, tipType, text, name));
  }

  requestAddMarker(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                   image: FlatModels.ImageEntityModels.Image | null,
                   poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                   tipType: FlatModels.AnnotationEntityModels.TipType,
                   text: string | null,
                   currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                   name: string) {
    // Add Marker
    return FlatFacades.AnnotationEntityFacade.addMarker(this.store, uuid(), page, image, poi, tipType, currentPositionAndZoom, text).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      )
    );
  }

  requestUpdateMarkerPosition(annotation: FlatModels.AnnotationEntityModels.Marker,
                              currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Update Marker Position
    return FlatFacades.AnnotationEntityFacade.updateMarkerPosition(this.store, annotation, currentPositionAndZoom).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.annotation_update_position',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      )
    );
  }

  requestAddFreeFormWithPosition(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                                 image: FlatModels.ImageEntityModels.Image | null,
                                 poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                                 shape: FlatModels.AnnotationEntityModels.Shape,
                                 text: string | null,
                                 name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.AddFreeFormWithPosition(page, image, poi, shape, text, name));
  }

  requestAddFreeForm(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                     image: FlatModels.ImageEntityModels.Image | null,
                     poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                     shape: FlatModels.AnnotationEntityModels.Shape,
                     text: string | null,
                     currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                     name: string) {
    // Add Free Form
    return FlatFacades.AnnotationEntityFacade.addFreeForm(this.store, page, image, poi, shape, currentPositionAndZoom, text).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateFreeFormPosition(annotation: FlatModels.AnnotationEntityModels.FreeForm,
                                currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Update Free Form Position
    return FlatFacades.AnnotationEntityFacade.updateFreeFormPosition(this.store, annotation, currentPositionAndZoom).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.annotation_update_position',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      )
    );
  }

  requestAddKeymapWithPosition(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                               image: FlatModels.ImageEntityModels.Image | null,
                               poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                               text: string | null,
                               name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.AddKeymapWithPosition(page, image, poi, text, name));
  }

  requestAddKeymap(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                   image: FlatModels.ImageEntityModels.Image | null,
                   poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                   text: string | null,
                   currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                   name: string) {
    // Add Keymap
    return FlatFacades.AnnotationEntityFacade.addKeymap(this.store, page, image, poi, currentPositionAndZoom, text).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateKeymapPosition(annotation: FlatModels.AnnotationEntityModels.Keymap,
                              currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Update Keymap Position
    return FlatFacades.AnnotationEntityFacade.updateKeymapPosition(this.store, annotation, currentPositionAndZoom).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.annotation_update_position',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      )
    );
  }

  requestAddTextWithPosition(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                             image: FlatModels.ImageEntityModels.Image | null,
                             poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                             text: string | null,
                             name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.AddTextWithPosition(page, image, poi, text, name));
  }

  requestAddText(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                 image: FlatModels.ImageEntityModels.Image | null,
                 poi: FlatModels.PointOfInterestEntityModels.PointOfInterest | null,
                 text: string | null,
                 currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                 name: string) {
    // Add Marker
    return FlatFacades.AnnotationEntityFacade.addMarker(this.store, uuid(), page, image, poi, FlatModels.AnnotationEntityModels.TipType.enum.none, currentPositionAndZoom, text || 'Neuer Text', true).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      )
    );
  }
  
  requestUpdateTextPosition(annotation: FlatModels.AnnotationEntityModels.Marker | FlatModels.AnnotationEntityModels.Text,
                            currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Update Text Position
    return FlatFacades.AnnotationEntityFacade.updateMarkerPosition(this.store, annotation, currentPositionAndZoom).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.annotation_update_position',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      )
    );
  }

  requestUpdateMarker(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType,
                      annotationSource: FlatModels.AnnotationSourceEntityModels.AnnotationSource,
                      annotationTargets: FlatModels.AnnotationTargetEntityModels.AnnotationTarget[],
                      name: string) {
    // Update Marker
    return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(structuredClone(annotation))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(structuredClone(annotationSource)))
      ),
      mergeMap(() =>
        forkJoin(from(annotationTargets).pipe(map((annotationTarget) => this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(annotationTarget)))))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateFreeForm(annotation: FlatModels.AnnotationEntityModels.FreeForm,
                        annotationSource: FlatModels.AnnotationSourceEntityModels.AnnotationSource,
                        freeFormStyle: FlatModels.FreeFormStyleEntityModels.FreeFormStyle,
                        name: string) {
    // Update FreeForm
    return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(structuredClone(annotation))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(structuredClone(annotationSource)))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.FreeFormStyleEntityActions.UpdateEntity(structuredClone(freeFormStyle)))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType,
                          name: string) {
    // Update Annotation
    return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(structuredClone(annotation))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateAnnotationSource(annotationSource: FlatModels.AnnotationSourceEntityModels.AnnotationSource,
                                name: string) {
    // Update Annotation Source
    return this.store.dispatch(new FlatActions.AnnotationSourceEntityActions.UpdateEntity(structuredClone(annotationSource))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateAnnotationLabel(annotationLabel: FlatModels.AnnotationLabelEntityModels.AnnotationLabel,
                               name: string) {
    // Update AnnotationLabel
    return this.store.dispatch(new FlatActions.AnnotationLabelEntityActions.UpdateEntity(annotationLabel)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateAnnotationTarget(annotationTarget: FlatModels.AnnotationTargetEntityModels.AnnotationTarget,
                                name: string) {
    // Update Annotation Target
    return this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(structuredClone(annotationTarget))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateMarkerAddTargetWithPosition(marker: FlatModels.AnnotationEntityModels.Marker) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.UpdateMarkerAddTargetWithPosition(marker));
  }

  requestUpdateMarkerAddTarget(marker: FlatModels.AnnotationEntityModels.Marker,
                               currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom) {
    // Calculate Size And Position
    const scale = currentPositionAndZoom.isVM ? currentPositionAndZoom.currentPosition.zoom : 1;
    const aspectRatio = currentPositionAndZoom.currentPosition.width / currentPositionAndZoom.currentPosition.height;
    
    // Get Data
    const typedAnnotationTargets = this.store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
    const typedAnnotationSources = this.store.selectSnapshot(FlatStates.AnnotationSourceEntityState.typedEntities$);
    
    // Get Annotation Target
    const annotationTarget = typedAnnotationTargets[marker.targets[0]];
    let tipType: FlatModels.AnnotationEntityModels.TipType = FlatModels.AnnotationEntityModels.TipType.enum.dot;
    if (annotationTarget) {
      tipType = annotationTarget.endType;
    }
    
    // Get Annotation Source
    const annotationSource = typedAnnotationSources[marker.source];
    
    // Create New Annotation Target
    let newMarkerTarget: FlatModels.AnnotationTargetEntityModels.AnnotationTarget | undefined;
    const lastTarget = typedAnnotationTargets[marker.targets[marker.targets.length - 1]];
    const sourcePosition = { x: annotationSource?.position?.x || 0.5, y: annotationSource?.position?.y || 0.5 };
    
    if (lastTarget) {
      const targetPosition = { x: lastTarget.position?.x || 0.5, y: lastTarget.position?.y || 0.5 };
      const rotationAngle = -10 / 180 * Math.PI;
      const delta = { x: targetPosition.x - sourcePosition.x, y: targetPosition.y - sourcePosition.y };
      const rotatedDelta = {
        x: delta.x * Math.cos(rotationAngle) - delta.y * Math.sin(rotationAngle),
        y: delta.x * Math.sin(rotationAngle) + delta.y * Math.cos(rotationAngle)
      }
      newMarkerTarget = structuredClone(lastTarget);
      newMarkerTarget.id = uuid();
      newMarkerTarget.position = {
        x: sourcePosition.x + rotatedDelta.x,
        y: sourcePosition.y + rotatedDelta.y
      }
    } else {
      // Setup Offset
      const offset = {
        x: -0.025,
        y: 0.15,
      }
      if (offset.x === 0 && offset.y === 0) {
        offset.x = -0.025 / scale;
        offset.y = 0.15 * aspectRatio / scale;
      }
      
      // Create Marker Target
      newMarkerTarget = FlatFacades.AnnotationTargetEntityFacade.getNewInstance(uuid(), marker, tipType, {x: sourcePosition.x + offset.x, y: sourcePosition.y + offset.y});
    }
    
    // Update Annotation
    marker.targets.push(newMarkerTarget.id);
    
    // Add Target And Update Marker
    return this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.AddEntity(newMarkerTarget)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(marker))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.marker_target_add',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateMarkerRemoveTarget(marker: FlatModels.AnnotationEntityModels.Marker) {
    // Get Data
    const typedAnnotationTargets = this.store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
    
    // Get Annotation Target
    const annotationTarget = typedAnnotationTargets[marker.targets[marker.targets.length - 1]];
    
    // Update Annotation
    marker.targets.splice(marker.targets.findIndex(id => id === annotationTarget.id));
    
    // Remove Target And Update Marker
    return this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.RemoveEntity(annotationTarget)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(marker))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.marker_target_remove',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateMarkerChangeEndType(marker: FlatModels.AnnotationEntityModels.Marker,
                                   endType: FlatModels.AnnotationEntityModels.TipType) {
    // Get Data
    const typedAnnotationTargets = this.store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
    
    // Update Annotation Targets
    const annotationTargets = []
    for (const id of marker.targets) {
      // Get Annotation Target
      const annotationTarget = structuredClone(typedAnnotationTargets[id]);
      
      // Update Annotation Target
      annotationTarget.endType = endType
      
      // Add To Change Array
      annotationTargets.push(annotationTarget);
    }
    
    // Update Targets
    return of(annotationTargets).pipe(
      switchMap((annotationTargets) => {
        return forkJoin(annotationTargets.map((annotationTarget) => this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(annotationTarget))));
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.marker_target_update_end_type',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestUpdateMarkerChangeEndRadius(marker: FlatModels.AnnotationEntityModels.Marker,
                                     endRadius: number) {
    // Get Data
    const typedAnnotationTargets = this.store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
    
    // Update Annotation Targets
    const annotationTargets = []
    for (const id of marker.targets) {
      // Get Annotation Target
      const annotationTarget = structuredClone(typedAnnotationTargets[id]);
      
      // Update Annotation Target
      annotationTarget.endRadius = endRadius
      
      // Add To Change Array
      annotationTargets.push(annotationTarget);
    }
    
    // Update Targets
    return of(annotationTargets).pipe(
      switchMap((annotationTargets) => {
        return forkJoin(annotationTargets.map((annotationTarget) => this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(annotationTarget))));
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.marker_target_update_end_radius',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateMarkerChangeColor(marker: FlatModels.AnnotationEntityModels.Marker,
                                 color: string) {
    // Get Data
    const typedAnnotationTargets = this.store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
    
    // Update Annotation Targets
    const annotationTargets = []
    for (const id of marker.targets) {
      // Get Annotation Target
      const annotationTarget = structuredClone(typedAnnotationTargets[id]);
      
      // Update Annotation Target
      annotationTarget.color = color
      
      // Add To Change Array
      annotationTargets.push(annotationTarget);
    }
    
    // Update Targets
    return of(annotationTargets).pipe(
      switchMap((annotationTargets) => {
        return forkJoin(annotationTargets.map((annotationTarget) => this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(annotationTarget))));
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.marker_target_update_color',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateMarkerTargets(marker: FlatModels.AnnotationEntityModels.Marker,
                             color: string,
                             endType: FlatModels.AnnotationEntityModels.TipType,
                             endRadius: number) {
    // Get Data
    const typedAnnotationTargets = this.store.selectSnapshot(FlatStates.AnnotationTargetEntityState.typedEntities$);
    
    // Update Annotation Targets
    const annotationTargets = []
    for (const id of marker.targets) {
      // Get Annotation Target
      const annotationTarget = structuredClone(typedAnnotationTargets[id]);
      
      // Update Annotation Target
      annotationTarget.endType = endType;
      annotationTarget.endRadius = endRadius;
      annotationTarget.color = color;
      
      // Add To Change Array
      annotationTargets.push(annotationTarget);
    }
    
    // Update Targets
    return of(annotationTargets).pipe(
      switchMap((annotationTargets) => {
        return forkJoin(annotationTargets.map((annotationTarget) => this.store.dispatch(new FlatActions.AnnotationTargetEntityActions.UpdateEntity(annotationTarget))));
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.marker_target_update_color',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateFreeFormStyle(freeFormStyle: FlatModels.FreeFormStyleEntityModels.FreeFormStyle,
                             name: string) {
    // Update Free Form Style
    return this.store.dispatch(new FlatActions.FreeFormStyleEntityActions.UpdateEntity(structuredClone(freeFormStyle))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateKeymapStyle(keymapStyle: FlatModels.KeymapStyleEntityModels.KeymapStyle,
                           name: string) {
    // Update Keymap Style
    return this.store.dispatch(new FlatActions.KeymapStyleEntityActions.UpdateEntity(structuredClone(keymapStyle))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdateKeymapLabel(keymapLabel: FlatModels.KeymapLabelEntityModels.KeymapLabel,
                           name: string) {
    // Update Keymap Label
    return this.store.dispatch(new FlatActions.KeymapLabelEntityActions.UpdateEntity(structuredClone(keymapLabel))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddKeymapColumn(keymap: FlatModels.AnnotationEntityModels.Keymap,
                         name: string) {
    // Create New Keymap Column
    const newKeymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn = {
      id: uuid(),
      order: keymap.columns?.length || 0,
      keymap: keymap.id,
      labels: [],
    };
    
    // Update Keymap
    keymap.columns.push(newKeymapColumn.id);
    
    // Add Keymap Column as Update Keymap
    return this.store.dispatch(new FlatActions.KeymapColumnEntityActions.AddEntity(newKeymapColumn)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(keymap))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveKeymapColumn(keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn,
                            name: string) {
    return FlatFacades.KeymapColumnEntityFacade.removeKeymapColumn(this.store, keymapColumn.id).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddKeymapLabel(keymapColumn: FlatModels.KeymapColumnEntityModels.KeymapColumn,
                        name: string) {
    // Create New Keymap Label
    const newKeymapLabel: FlatModels.KeymapLabelEntityModels.KeymapLabel = {
      id: uuid(),
      order: keymapColumn.labels?.length || 0,
      column: keymapColumn.id,
      index: '',
      text: '',
      annotation: null
    };
    
    // Update Keymap Column
    keymapColumn.labels.push(newKeymapLabel.id);
    
    // Add Keymap Label as Update Keymap Column
    return this.store.dispatch(new FlatActions.KeymapLabelEntityActions.AddEntity(newKeymapLabel)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.KeymapColumnEntityActions.UpdateEntity(keymapColumn))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestRemoveKeymapLabel(keymapLabel: FlatModels.KeymapLabelEntityModels.KeymapLabel,
                           name: string) {
    return FlatFacades.KeymapLabelEntityFacade.removeKeymapLabel(this.store, keymapLabel.id).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestUpdateAnnotationPosition(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.UpdateAnnotationPosition(annotation));
  }
  
  requestPasteAnnotationWithPosition(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                                     image: FlatModels.ImageEntityModels.Image) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.CopyAnnotationWithPosition(page, image));
  }

  requestCopyAnnotationWithPosition(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                                    image: FlatModels.ImageEntityModels.Image) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.CopyAnnotationWithPosition(page, image));
  }

  requestRemoveAnnotation(annotation: FlatModels.AnnotationEntityModels.AnnotationEntityType,
                          name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image)).pipe(
      mergeMap(() =>
        FlatFacades.AnnotationEntityFacade.removeAnnotation(this.store, annotation.id)
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  //</editor-fold>
  
  //<editor-fold desc="Group">

  requestSetGroupId(groupId: string | null) {
    return this.store.dispatch(new FlatActions.BulkEditAuthorActions.ResetSelection()).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(groupId))
      ),
      mergeMap(() => {
        if (groupId === null) {
          return this.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.metadata)
        }
        return this.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.group)
      })
    );
  }

  requestAddGroup(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                  text: string | null,
                  name: string) {
    // Create New Group
    const newGroup: FlatModels.GroupEntityModels.Group = {
      id: uuid(),
      order: page.groups?.length || 0,
      name: text || 'Neue Gruppe',
      annotations: [],
      color: null,
      description: null,
      slide: page.id,
    }
    
    // Update Page
    page.groups.push(newGroup.id);
    
    // Add Group And Update Page
    return this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.AddEntity(newGroup)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((data) => {
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(newGroup.id))
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.group))
        this.store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newGroup.id));
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestUpdateGroup(group: FlatModels.GroupEntityModels.Group,
                     name: string) {
    // Update Group
    return this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(group)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveGroup(group: FlatModels.GroupEntityModels.Group,
                     name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.metadata)).pipe(
      mergeMap(() =>
        FlatFacades.GroupEntityFacade.removeGroup(this.store, group.id)
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddAnnotationIdToGroup(group: FlatModels.GroupEntityModels.Group,
                                annotationId: string,
                                name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Annotation
    const annotation = structuredClone(typedAnnotations[annotationId]);
    if (annotation.groups) {
      if (annotation.groups.findIndex(g => g === group.id) === -1) {
        annotation.groups.push(group.id);
      }
    } else {
      annotation.groups = [group.id];
    }
    
    // Update Group
    group.annotations.push(annotationId);
    
    // Update Group
    return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(group))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestAddAnnotationIdsToGroup(group: FlatModels.GroupEntityModels.Group,
                                 annotationIds: string[],
                                 name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Annotations
    const annotations = [];
    for (const annotationId of annotationIds) {
      // Get Annotation
      const annotation = structuredClone(typedAnnotations[annotationId]);
      if (annotation.groups) {
        if (annotation.groups.findIndex(g => g === group.id) === -1) {
          annotation.groups.push(group.id);
        }
      } else {
        annotation.groups = [group.id];
      }
      annotations.push(annotation);
      
      // Update Group
      group.annotations.push(annotationId);
    }
    
    // Update Group
    return forkJoin(annotations.map(annotation => this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation)))).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(group))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestAddAnnotationIdToGroups(groupIds: string[],
                                 annotationId: string,
                                 name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    const typedGroups = this.store.selectSnapshot(FlatStates.AnnotationGroupEntityState.typedEntities$);
    
    // Update Groups
    const annotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[] = [];
    const groups: FlatModels.GroupEntityModels.Group[] = [];
    for (const groupId of groupIds) {
      // Get Annotation
      const annotation = structuredClone(typedAnnotations[annotationId]);
      if (annotation.groups) {
        if (annotation.groups.findIndex(g => g === groupId) === -1) {
          annotation.groups.push(groupId);
        }
      } else {
        annotation.groups = [groupId];
      }
      annotations.push(annotation);
      
      // Update Group
      const group = structuredClone(typedGroups[groupId]);
      group.annotations.push(annotationId);
      groups.push(group);
    }
    
    // Update Groups
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(annotations.map(annotation => this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation))))
      ),
      mergeMap(() =>
        forkJoin(groups.map(group => this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(group))))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveAnnotationFromGroup(group: FlatModels.GroupEntityModels.Group,
                                   annotationId: string,
                                   name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    
    // Get Annotation
    const annotation = structuredClone(typedAnnotations[annotationId]);
    if (annotation.groups) {
      annotation.groups.splice(annotation.groups.findIndex(g => g === group.id), 1);
    } else {
      annotation.groups = [];
    }
    
    // Update Group
    group.annotations.splice(group.annotations.findIndex(a => a === annotationId), 1);
    
    // Update Group
    return this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(group))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveAnnotationFromGroups(groupIds: string[],
                                    annotationId: string,
                                    name: string) {
    // Get Data
    const typedAnnotations = this.store.selectSnapshot(FlatStates.AnnotationEntityState.typedEntities$);
    const typedGroups = this.store.selectSnapshot(FlatStates.AnnotationGroupEntityState.typedEntities$);
    
    // Update Groups
    const annotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[] = [];
    const groups: FlatModels.GroupEntityModels.Group[] = [];
    for (const groupId of groupIds) {
      // Get Annotation
      const annotation = structuredClone(typedAnnotations[annotationId]);
      if (annotation.groups) {
        annotation.groups.splice(annotation.groups.findIndex(g => g === groupId), 1);
      } else {
        annotation.groups = [];
      }
      annotations.push(annotation);
      
      // Update Group
      const group = structuredClone(typedGroups[groupId]);
      group.annotations.splice(group.annotations.findIndex(a => a === annotationId), 1);
      groups.push(group);
    }
    
    // Update Groups
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(annotations.map(annotation => this.store.dispatch(new FlatActions.AnnotationEntityActions.UpdateEntity(annotation))))
      ),
      mergeMap(() =>
        forkJoin(groups.map(group => this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.UpdateEntity(group))))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  //</editor-fold>
  
  //<editor-fold desc="Poi">
  
  requestSetPoiId(poiId: string) {
    // Get Data
    const currentSelectedImage = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImage$);
    const images = this.store.selectSnapshot(FlatStates.ImageEntityState.imagesWithPoi$(poiId));
    
    // Check Image
    let changeImage = false;
    let image = currentSelectedImage as FlatModels.ImageEntityModels.Image;
    if (images.length > 0 && poiId !== null) {
      if (currentSelectedImage && !images.map(i => i.id).includes(currentSelectedImage.id)) {
        changeImage = true;
        image = images[0];
      }
    }
    
    return of({}).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.BulkEditAuthorActions.ResetSelection())
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.QuestionEntityActions.SetQuestionId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationGroupEntityActions.SetGroupId(null))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.AnnotationEntityActions.SetAnnotationId(null))
      ),
      mergeMap((data) => {
        if (changeImage) {
          let layers: FlatModels.LayerEntityModels.Layer[] = [];
          if (images.length > 0) {
            layers = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.layersWithImage$(image.id));
          }
          
          const currentSelectedSlideLayersMap = this.store.selectSnapshot(FlatStates.SlideLayerEntityState.currentSelectedSlideLayersMap$);
          let layerIndex = layers.length > 0 ? currentSelectedSlideLayersMap.indexOf(layers[0].id) : 0;
          if (!Number.isInteger(layerIndex) || layerIndex < 0) {
            layerIndex = 0
          }
          return this.store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layerIndex));
        }
        return of(data);
      }),
      mergeMap((data) => {
        if (changeImage) {
          const currentSelectedSlideImagesMap = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImagesMap$);
          let imageIndex = images.length > 0 ? currentSelectedSlideImagesMap.indexOf(image.id) : 0;
          if (!Number.isInteger(imageIndex) || imageIndex < 0) {
            imageIndex = 0
          }
          return this.store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(imageIndex));
        }
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(poiId))
      ),
      mergeMap(() => {
        if (poiId === null) {
          return this.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.metadata)
        }
        return this.requestSetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.poi)
      }),
    );
  }

  requestRearrangeImagePois(image: FlatModels.ImageEntityModels.Image,
                            pois: FlatModels.PointOfInterestEntityModels.PointOfInterest[]) {
    // Rearrange Pois
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.RearrangeImagePois(image, pois)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.image_rearrange_pois',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRearrangePoiAnnotations(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                                 annotationToPointOfInterests: FlatModels.PointOfInterestEntityModels.AnnotationToPointOfInterest[]) {
    // Rearrange Poi Annotations
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.RearrangePoiAnnotations(poi, annotationToPointOfInterests)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.poi_rearrange_annotations',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddPoiWithPosition(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                            image: FlatModels.ImageEntityModels.Image | null,
                            text: string | null,
                            name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.AddPoiWithPosition(page, image, text, name));
  }
  
  requestAddPoi(page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                image: FlatModels.ImageEntityModels.Image | null,
                text: string | null,
                currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                name: string) {
    // Add Poi
    return FlatFacades.PoiEntityFacade.addPoi(this.store, uuid(), page, image, currentPositionAndZoom, text).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      )
    );
    /*
    // Create New Poi
    const newPoi: FlatModels.PointOfInterestEntityModels.PointOfInterest = {
      id: uuid(),
      order: page.pois.length,
      name: text || 'Neuer Poi',
      notes: [],
      bounds: {
        x: currentPositionAndZoom.currentBounds.x,
        y: currentPositionAndZoom.currentBounds.y,
        width: currentPositionAndZoom.currentBounds.width,
        height: currentPositionAndZoom.currentBounds.height,
      },
      position: {
        x: currentPositionAndZoom.currentPosition.x,
        y: currentPositionAndZoom.currentPosition.y,
      },
      annotationToPointOfInterest: [],
      zoom: null,
      slide: page.id,
    }
    
    // Update Image
    if (image) {
      image.pois.push(newPoi.id)
    }
    
    // Update Page
    page.pois.push(newPoi.id);
    
    // Add Poi And Update Page
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.AddEntity(newPoi)).pipe(
      mergeMap((data) => {
        if (image) {
          return this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
        }
        return of(data);
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(newPoi.id))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.poi))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
    */
  }

  requestUpdatePoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                   name: string) {
    // Update Poi
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestUpdatePoiPosition(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest) {
    // Update Poi Position
    return this.store.dispatch(new FlatActions.SlideAuthorActions.UpdatePoiPosition(poi));
  }
  
  requestRemovePoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                   name: string) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.image)).pipe(
      mergeMap(() =>
        FlatFacades.PoiEntityFacade.removePoi(this.store, poi.id)
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAddAnnotationIdToPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                              annotationId: string,
                              name: string) {
    // Update Poi
    if (poi.annotationToPointOfInterest.findIndex(atpoi => atpoi.annotation === annotationId && atpoi.pointOfInterest === poi.id) === -1) {
      poi.annotationToPointOfInterest.push({
        id: uuid(),
        order: poi.annotationToPointOfInterest?.length || 0,
        annotation: annotationId,
        pointOfInterest: poi.id,
      });
    }
    
    // Update Poi
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestAddAnnotationIdsToPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                               annotationIds: string[],
                               name: string) {
    for (const annotationId of annotationIds) {
      // Update Poi
      if (poi.annotationToPointOfInterest.findIndex(atpoi => atpoi.annotation === annotationId && atpoi.pointOfInterest === poi.id) === -1) {
        poi.annotationToPointOfInterest.push({
          id: uuid(),
          order: poi.annotationToPointOfInterest?.length || 0,
          annotation: annotationId,
          pointOfInterest: poi.id,
        });
      }
    }
    
    // Update Poi
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestAddAnnotationIdToPois(poiIds: string[],
                               annotationId: string,
                               name: string) {
    // Get Data
    const typedPois = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    
    // Update Pois
    const pois = [];
    for (const poiId of poiIds) {
      const poi = structuredClone(typedPois[poiId]);
      if (poi.annotationToPointOfInterest.findIndex(atpoi => atpoi.annotation === annotationId && atpoi.pointOfInterest === poi.id) === -1) {
        poi.annotationToPointOfInterest.push({
          id: uuid(),
          order: poi.annotationToPointOfInterest?.length || 0,
          annotation: annotationId,
          pointOfInterest: poi.id,
        });
      }
      pois.push(poi);
    }
    
    // Update Pois
    return of(pois).pipe(
      mergeMap((pois) =>
        forkJoin(pois.map(poi => this.store.dispatch(new FlatActions.PointOfInterestEntityActions.UpdateEntity(poi))))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  requestRemoveAnnotationFromPoi(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                                 annotationId: string,
                                 name: string) {
    // Update Poi
    poi.annotationToPointOfInterest = poi.annotationToPointOfInterest.filter((atpoi) => !(atpoi.annotation === annotationId && atpoi.pointOfInterest === poi.id));
    
    // Update Poi
    return this.store.dispatch(new FlatActions.PointOfInterestEntityActions.RearrangePoiAnnotations(poi, poi.annotationToPointOfInterest)).pipe(
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestRemoveAnnotationFromPois(poiIds: string[],
                                  annotationId: string,
                                  name: string) {
    // Get Data
    const typedPois = this.store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    
    // Update Pois
    const pois = [];
    for (const poiId of poiIds) {
      const poi = structuredClone(typedPois[poiId]);
      poi.annotationToPointOfInterest = poi.annotationToPointOfInterest.filter((atpoi) => !(atpoi.annotation === annotationId && atpoi.pointOfInterest === poi.id));
      pois.push(poi);
    }
    
    // Update Pois
    return of(pois).pipe(
      mergeMap((pois) =>
        forkJoin((pois.map(poi =>
          this.store.dispatch(new FlatActions.PointOfInterestEntityActions.RearrangePoiAnnotations(poi, poi.annotationToPointOfInterest))
        )))
      ),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          name,
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }

  requestAssignAnnotationsToPoiImages(poi: FlatModels.PointOfInterestEntityModels.PointOfInterest,
                                      removeOnOtherImages: boolean) {
    // Get Data
    const currentSelectedSlideImages = this.store.selectSnapshot(FlatStates.ImageEntityState.currentSelectedSlideImages$);
    
    // Get Poi Annotations
    const poiAnnotations = poi.annotationToPointOfInterest.map(atpoi => atpoi.annotation);
    
    // Update Poi Images
    const images = currentSelectedSlideImages.reduce((arr, image) => {
      if (image.pois.includes(poi.id)) {
        let changed = false;
        image = structuredClone(image);
        poiAnnotations.forEach((poiAnnotation) => {
          if (!image.annotations.includes(poiAnnotation)) {
            image.annotations.push(poiAnnotation);
            changed = true;
          }
        });
        if (changed) {
          arr.push(image);
        }
      } else if (removeOnOtherImages) {
        image = structuredClone(image);
        image.annotations = image.annotations.filter((a) => !poiAnnotations.includes(a));
        arr.push(image);
      }
      return arr;
    }, [] as FlatModels.ImageEntityModels.Image[]);
    
    // Update Images
    return of({}).pipe(
      mergeMap(() => {
        if (images.length === 0) {
          throw new FlatModels.ErrorControlModels.MedsurfError(
            'notification.no_data_synchronized',
            FlatModels.ErrorControlModels.ErrorLevel.enum.warning,
            FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
            "LIBS.FlatStates.SlideAuthorFacade.requestAssignAnnotationsToPoiImages"
          );
        }
        return forkJoin(images.map(image => this.store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))));
      }),
      mergeMap(() =>
        this.store.dispatch(new FlatActions.HistoryControlActions.FinishSnapshot(
          'history.images_synchronize',
          this.store.selectSnapshot(FlatStates.NavigationControlState.currentUrl$)
        ))
      ),
    );
  }
  
  //</editor-fold>
  

  requestSetEditPanelDisplay(editPanelDisplay: FlatModels.SlideAuthorModels.EditPanelDisplay) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(editPanelDisplay));
  }

  requestSetMainPanelDisplay(mainPanelDisplay: FlatModels.SlideAuthorModels.MainPanelDisplay) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetMainPanelDisplay(mainPanelDisplay));
  }

  requestSetIsSplitterMoving(isSplitterMoving: boolean) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.SetIsSplitterMoving(isSplitterMoving));
  }

  requestActivateDrawTool(freeform: FlatModels.AnnotationEntityModels.FreeForm) {
    return this.store.dispatch(new FlatActions.SlideAuthorActions.ActivateDrawTool(freeform));
  }
  
  //</editor-fold>

  //</editor-fold>

}
